import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import { seatingApi } from '../../../services/seating-api-client';
import { fetchTrackerBoard } from '../../../services/tracker-api-client';
import { seatingChartKey, trackerBoardKey } from '../../../lib/groupQueryKeys';
import { isVersionConflict, seatingErrorMessage } from '../../../lib/seatingErrors';
import { resolveBonds } from '../../../lib/seatingBonds';
import {
  isNoOp,
  planAutoSeat,
  placementsByTable,
  toSeatBatch,
  type AutoSeatMode,
  type LockedChair,
  type SeatGrouping,
  type SeatPattern,
} from '../../../lib/autoSeat';
import { lockCounts, lockFromPlan, partyKeyOf, seatedParties } from '../../../lib/autoSeatLocks';
import { SEAT_PATTERN_LABELS } from '../../../lib/seatRelation';
import { newSeed } from '../../../lib/seededRandom';
import { resolveSeatGroups } from '../../../lib/seatingGroups';
import type { SeatingChartDetail } from '../../../types/seating';

/** How many soft-rule notes to print before "and N more". */
const VISIBLE_WARNINGS = 5;

/**
 * Auto-seat: a PROPOSAL, read before anything is written.
 *
 * Opening this reads the room fresh (never the copy the editor happened to have
 * cached), draws a plan from it with `planAutoSeat`, and shows it. Nothing is
 * sent until Apply. Apply posts the plan as one batch carrying the chart version
 * the plan was drawn from, so if anyone touched the room in between the server
 * answers 409 and applies NOTHING — the host is told and asked to look again.
 *
 * The default is "Fill empty seats only": everyone the host already placed stays
 * exactly where they are. "Re-seat everyone" is the explicit, named alternative.
 *
 * The proposal is a SHUFFLE, not an alphabet: it is drawn from a random seed picked
 * when the dialog opens, and "Shuffle" picks another. The seed is state, so the
 * preview and what Apply writes are one plan. Guests can be LOCKED (the pin beside
 * a name) — the host's core people — and Shuffle then re-draws only everyone else.
 * Locks live in this dialog's state only; Apply writes the plan as previewed.
 */
export default function AutoSeatDialog({
  groupId,
  chartId,
  onClose,
  onApplied,
}: {
  groupId: string;
  chartId: string;
  onClose: () => void;
  /** The chair list and the chart picker are both stale after a batch. */
  onApplied: () => void;
}) {
  const [mode, setMode] = useState<AutoSeatMode>('fill_empty_seats');
  const [includeUnconfirmed, setIncludeUnconfirmed] = useState(false);
  const [grouping, setGrouping] = useState<SeatGrouping>('by_relationship');
  const [pattern, setPattern] = useState<SeatPattern>('side_by_side');
  const [seed, setSeed] = useState(newSeed);
  const [locks, setLocks] = useState<Map<string, LockedChair[]>>(new Map());

  // Always re-read on open: the proposal is only as good as the room it was drawn from.
  const chartQuery = useQuery<SeatingChartDetail>(
    seatingChartKey(groupId, chartId),
    () => seatingApi.getChart(groupId, chartId),
    { refetchOnMount: 'always' },
  );
  // Keep-together / keep-apart live on the guest board, not on the chart.
  const boardQuery = useQuery(trackerBoardKey(groupId), () => fetchTrackerBoard(groupId), {
    refetchOnMount: 'always',
  });

  const detail = chartQuery.data;
  const board = boardQuery.data && boardQuery.data.ok ? boardQuery.data.data : null;
  const boardFailed = !boardQuery.isLoading && !boardQuery.isFetching && !board;
  const reading = chartQuery.isFetching || boardQuery.isFetching;
  const rosterChart = !detail?.chart.dinnerHoldId;

  const groups = useMemo(() => resolveSeatGroups(board?.guests, board?.tags), [board]);
  const plan = useMemo(() => {
    if (!detail || reading) return null;
    return planAutoSeat({
      mode,
      sourceVersion: detail.chart.sourceVersion,
      tables: detail.tables,
      candidates: detail.candidates,
      bonds: resolveBonds(board?.links, board?.guests),
      includeUnconfirmed,
      seed,
      grouping,
      pattern,
      groups,
      locks: [...locks.values()].flat(),
    });
  }, [detail, reading, mode, includeUnconfirmed, board, seed, grouping, pattern, groups, locks]);

  const applyMutation = useMutation(
    () => seatingApi.applySeatBatch(groupId, chartId, toSeatBatch(plan!), plan!.sourceVersion),
    { onSuccess: onApplied },
  );

  const conflict = isVersionConflict(applyMutation.error);
  const failure = applyMutation.error
    ? conflict
      ? 'The room changed after this proposal was drawn, so nothing was applied. Look at it again below.'
      : seatingErrorMessage(applyMutation.error)
    : '';
  const saving = applyMutation.isLoading;
  const grouped = plan && detail ? placementsByTable(plan, detail.tables) : [];
  const people = plan ? new Set(plan.placements.filter((c) => !c.companion).map((c) => c.name)).size : 0;
  const unconfirmedCount = (detail?.candidates ?? []).filter(
    (c) => !c.claimStatus && String(c.rsvpStatus ?? '').toLowerCase() !== 'going',
  ).length;

  // Locks are pinned to a plan's chairs, and a different mode (or guest list) is a different plan.
  const pick = (next: AutoSeatMode) => {
    applyMutation.reset();
    setLocks(new Map());
    setMode(next);
  };

  const reread = () => {
    applyMutation.reset();
    setLocks(new Map());
    chartQuery.refetch();
    boardQuery.refetch();
  };

  const shuffle = () => {
    applyMutation.reset();
    setSeed(newSeed());
  };

  const toggleLock = (key: string | null) => {
    if (!key || !plan) return;
    applyMutation.reset();
    setLocks((current) => {
      const next = new Map(current);
      if (next.has(key)) next.delete(key);
      else next.set(key, lockFromPlan(plan, key));
      return next;
    });
  };

  const seated = useMemo(() => seatedParties(detail?.tables ?? []), [detail]);
  const counts = plan ? lockCounts(plan) : null;
  const tagsMissing = Boolean(board) && !board?.tags;
  const noGroupsTagged = Boolean(board?.tags) && groups.size === 0;

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0.5 }}>Auto-seat</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            This is a proposal. Nothing changes until you press Apply.
          </Typography>

          <RadioGroup value={mode} onChange={(event) => pick(event.target.value as AutoSeatMode)}>
            <FormControlLabel
              value="fill_empty_seats"
              control={<Radio />}
              disabled={saving}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={700}>Fill empty seats only</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Everyone already in a chair stays exactly where they are.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="reseat_everyone"
              control={<Radio />}
              disabled={saving}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={700}>Re-seat everyone</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Starts the guests below over — including people you placed by hand. Blocked chairs
                    and anyone this doesn’t seat are left alone.
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>

          {rosterChart && unconfirmedCount > 0 ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeUnconfirmed}
                  disabled={saving}
                  onChange={(event) => { applyMutation.reset(); setLocks(new Map()); setIncludeUnconfirmed(event.target.checked); }}
                />
              }
              label={`Also seat the ${unconfirmedCount} who haven’t said they’re going`}
            />
          ) : null}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              select
              size="small"
              fullWidth
              label="Seat guests"
              value={grouping}
              disabled={saving}
              onChange={(event) => { applyMutation.reset(); setGrouping(event.target.value as SeatGrouping); }}
            >
              <MenuItem value="by_relationship">Group by relationship</MenuItem>
              <MenuItem value="mix_groups">Mix groups</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              fullWidth
              label="Pairs sit"
              value={pattern}
              disabled={saving}
              onChange={(event) => { applyMutation.reset(); setPattern(event.target.value as SeatPattern); }}
            >
              {(Object.keys(SEAT_PATTERN_LABELS) as SeatPattern[]).map((value) => (
                <MenuItem key={value} value={value}>{SEAT_PATTERN_LABELS[value]}</MenuItem>
              ))}
            </TextField>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {grouping === 'mix_groups'
              ? 'Households and parties stay together; each relationship group is spread across the tables; the rest is shuffled.'
              : 'Households and parties stay together; relationship groups sit together; the rest is shuffled.'}
          </Typography>
          {tagsMissing || noGroupsTagged ? (
            <Typography variant="caption" color="text.secondary">
              {tagsMissing
                ? 'Guest groups aren’t available yet, so everyone is treated as ungrouped.'
                : 'No guests are tagged as a Group or a Household, so everyone is shuffled as ungrouped. Tag guests on the Guests tab to keep families and friends together.'}
            </Typography>
          ) : null}

          {mode === 'reseat_everyone' && seated.length > 0 ? (
            <Autocomplete
              multiple
              size="small"
              options={seated}
              disabled={saving}
              value={seated.filter((entry) => locks.has(entry.key))}
              getOptionLabel={(entry) => entry.name}
              isOptionEqualToValue={(a, b) => a.key === b.key}
              onChange={(_, chosen) => {
                applyMutation.reset();
                setLocks(new Map(chosen.map((entry) => [entry.key, locks.get(entry.key) ?? entry.chairs])));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Keep these guests exactly where they are (optional)" />
              )}
            />
          ) : null}

          {chartQuery.isLoading || reading ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Reading the room…</Typography>
            </Stack>
          ) : null}
          {chartQuery.error ? <Alert severity="error">Couldn’t read the room, so nothing can be proposed.</Alert> : null}

          {boardFailed ? (
            <Alert severity="warning">
              Couldn’t read the guest links, so this proposal does <strong>not</strong> apply keep-together
              or keep-apart rules. Reload before applying if those matter.
            </Alert>
          ) : null}
          {detail?.chart.status === 'published' ? (
            <Alert severity="info">
              This chart is published. Guests will see their new seats as soon as you apply.
            </Alert>
          ) : null}
          {failure ? (
            <Alert
              severity={conflict ? 'warning' : 'error'}
              action={conflict ? <Button size="small" onClick={reread}>Propose again</Button> : undefined}
            >
              {failure}
            </Alert>
          ) : null}

          {plan ? (
            <>
              <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  {counts && counts.locked > 0
                    ? `Locked ${counts.locked} · shuffling ${counts.shuffling}`
                    : 'Pin a guest to lock them in place; Shuffle then re-draws everyone else.'}
                  {plan.droppedLocks > 0
                    ? ` ${plan.droppedLocks} lock${plan.droppedLocks === 1 ? '' : 's'} no longer applied.`
                    : ''}
                </Typography>
                <Button size="small" startIcon={<ShuffleIcon />} onClick={shuffle} disabled={saving}>
                  Shuffle
                </Button>
              </Stack>

              <Typography variant="subtitle2" fontWeight={700}>
                {isNoOp(plan)
                  ? 'Nothing to change.'
                  : mode === 'reseat_everyone'
                    ? `${plan.placements.length} chair${plan.placements.length === 1 ? '' : 's'} to fill for ${people} guest${people === 1 ? '' : 's'}; ${plan.unassign.length} to empty first.`
                    : `${plan.placements.length} chair${plan.placements.length === 1 ? '' : 's'} to fill for ${people} guest${people === 1 ? '' : 's'}.`}
                {plan.keptInPlace > 0 ? ` ${plan.keptInPlace} stay${plan.keptInPlace === 1 ? 's' : ''} where they are.` : ''}
              </Typography>

              {plan.overLimit ? (
                <Alert severity="error">
                  That is more than one apply can carry ({plan.placements.length + plan.unassign.length} changes; the
                  limit is 500). Fill part of the room by hand, or seat fewer guests, and try again.
                </Alert>
              ) : null}

              {grouped.map(({ table, chairs }) => (
                <Box key={table.id}>
                  <Typography variant="body2" fontWeight={700}>{table.label || 'Table'}</Typography>
                  <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {chairs.map((chair) => (
                      <Stack
                        component="li"
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        key={`${chair.tableId}:${chair.seatIndex}`}
                        sx={{ minHeight: 28 }}
                      >
                        <Typography variant="body2">
                          Seat {chair.seatIndex + 1} — {chair.name}
                          {chair.from ? (
                            <Typography component="span" variant="caption" color="text.secondary">
                              {' '}(was {chair.from.tableLabel}, seat {chair.from.seatIndex + 1})
                            </Typography>
                          ) : null}
                        </Typography>
                        {chair.group ? <Chip size="small" variant="outlined" label={chair.group} /> : null}
                        {chair.household ? <Chip size="small" variant="outlined" label={chair.household} /> : null}
                        {chair.companion ? (
                          chair.locked ? <PushPinIcon fontSize="inherit" color="primary" aria-label="Locked with their guest" /> : null
                        ) : (
                          <Tooltip title={chair.locked ? 'Locked here — click to unlock' : 'Lock this guest in this seat'}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={saving}
                                aria-pressed={chair.locked}
                                aria-label={`${chair.locked ? 'Unlock' : 'Lock'} ${chair.name} at ${table.label || 'Table'}, seat ${chair.seatIndex + 1}`}
                                onClick={() => toggleLock(partyKeyOf(chair))}
                              >
                                {chair.locked ? <PushPinIcon fontSize="inherit" color="primary" /> : <PushPinOutlinedIcon fontSize="inherit" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              ))}

              {plan.unseated.length > 0 ? (
                <Alert severity="warning">
                  <Typography variant="body2" fontWeight={700}>
                    Not seated ({plan.unseated.length})
                  </Typography>
                  <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {plan.unseated.map((entry, index) => (
                      <Typography component="li" variant="body2" key={`${entry.name}:${index}`}>
                        {entry.name}
                        {entry.seats > 1 ? ` (${entry.seats} chairs)` : ''} — {entry.reason}
                      </Typography>
                    ))}
                  </Stack>
                </Alert>
              ) : null}

              {plan.warnings.length > 0 ? (
                <Alert severity="info">
                  {plan.warnings.slice(0, VISIBLE_WARNINGS).map((warning) => (
                    <Typography variant="body2" key={warning}>{warning}</Typography>
                  ))}
                  {plan.warnings.length > VISIBLE_WARNINGS ? (
                    <Typography variant="body2">and {plan.warnings.length - VISIBLE_WARNINGS} more.</Typography>
                  ) : null}
                </Alert>
              ) : null}

              {plan.notConsidered > 0 && !includeUnconfirmed ? (
                <Typography variant="caption" color="text.secondary">
                  {plan.notConsidered} guest{plan.notConsidered === 1 ? '' : 's'} who haven’t said they’re going
                  {plan.notConsidered === 1 ? ' was' : ' were'} left out.
                </Typography>
              ) : null}
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!plan || saving || isNoOp(plan) || plan.overLimit}
          onClick={() => applyMutation.mutate()}
        >
          {saving ? 'Applying…' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
